/**
 * Post-processes AI-generated markdown to ensure math is rendered reliably
 * and currency amounts never break KaTeX parsing.
 */
export function fixMathDelimiters(content: string): string {
  if (!content) return content;

  const sanitizeMathInner = (input: string): string => {
    let cleaned = input;
    cleaned = cleaned.replace(/\\\{/g, "{").replace(/\\\}/g, "}");
    cleaned = cleaned.replace(
      /(?<!\\)\$(\d[\d,]*(?:\.\d+)?)/g,
      (_match, amount: string) => `\\$${amount}`,
    );
    return cleaned;
  };

  let result = normalizeRawMathSyntax(content);

  // Protect fenced code blocks
  const fencedBlocks: string[] = [];
  result = result.replace(/```[\s\S]*?```/g, (match) => {
    fencedBlocks.push(match);
    return `%%CODE_BLOCK_${fencedBlocks.length - 1}%%`;
  });

  // Protect inline code
  const inlineCodeBlocks: string[] = [];
  result = result.replace(/`[^`]+`/g, (match) => {
    inlineCodeBlocks.push(match);
    return `%%INLINE_CODE_${inlineCodeBlocks.length - 1}%%`;
  });

  // ===== STEP 0: Clean known broken AI patterns =====
  result = result.replace(/\\text\{\s*\}/g, "");
  result = result.replace(/\\text\{\$\}/g, "\\$");
  result = result.replace(/\\text\{\\\$\}/g, "\\$");
  result = result.replace(/\\text\{\$\}(\d)/g, (_m, d: string) => `\\$${d}`);
  result = result.replace(/\\text\{\\\$\}(\d)/g, (_m, d: string) => `\\$${d}`);
  // Fix \text{something$100} patterns
  result = result.replace(/\\text\{([^}]*)\$(\d[\d,]*(?:\.\d+)?)\}/g, (_m, pre: string, amount: string) => `\\text{${pre}\\$${amount}}`);
  // Fix broken \left/\right without matching delimiters
  result = result.replace(/\\left\s*([^(\[{|.\\\s])/g, "\\left($1");
  result = result.replace(/\\right\s*([^)\]}|.\\\s])/g, "\\right)$1");

  // Fix bare \0 (null char) that KaTeX renders as error
  result = result.replace(/\\0(?![0-9a-zA-Z])/g, "0");

  // Fix \DIGIT patterns like \10,000$ or \9,900$ → $10,000 or $9,900
  result = result.replace(/\\(\d[\d,]*(?:\.\d+)?)\$/g, (_m, num: string) => `\\$${num}`);
  // Also bare \DIGIT without trailing $ (like \10,000 alone)
  result = result.replace(/(?<![a-zA-Z])\\(\d[\d,]{2,}(?:\.\d+)?)/g, (_m, num: string) => `\\$${num}`);

  // Fix bare \times, \div, \pm etc. outside math delimiters (common in table cells)
  result = result.replace(
    /(?<!\$)\\(times|div|pm|mp|cdot|approx|neq|leq|geq|equiv|sim|propto|ll|gg)(?:\s*)(\d)/g,
    (_m, cmd: string, digit: string) => `$\\${cmd}$ ${digit}`,
  );
  // Bare \times not followed by digit
  result = result.replace(
    /(?<!\$)\\(times|div|pm|mp|cdot)(?![a-zA-Z{])(?!\$)/g,
    (_m, cmd: string) => `$\\${cmd}$`,
  );

  // Normalize escaped braces globally outside code blocks
  result = result.replace(/\\\{/g, "{").replace(/\\\}/g, "}");

  // ===== STEP 1: Convert \[...\] and \(...\) =====
  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner: string) => `$$${sanitizeMathInner(inner)}$$`);
  result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner: string) => `$${sanitizeMathInner(inner)}$`);

  // ===== STEP 2: Protect existing $$...$$ blocks =====
  const mathBlocks: string[] = [];
  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_m, inner: string) => {
    mathBlocks.push(`$$${sanitizeMathInner(inner)}$$`);
    return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`;
  });

  // ===== STEP 3: Escape currency BEFORE inline math detection =====
  result = result.replace(
    /(?<!\\)\$(\d[\d,]*(?:\.\d+)?)(\s*%?)\$/g,
    (_m, amount: string, suffix: string) => `\\$${amount}${suffix}`,
  );
  result = result.replace(
    /(?<!\\)\$(\d[\d,]*(?:\.\d+)?)(\s*%?)(?=[^\d]|$)/g,
    (_m, amount: string, suffix: string) => `\\$${amount}${suffix}`,
  );

  // ===== STEP 4: Protect valid inline math =====
  result = result.replace(/(?<!\\)\$((?:\\.|[^$\n])+?)\$/g, (match, inner: string) => {
    const trimmed = inner.trim();
    if (/^\d[\d,]*(?:\.\d+)?\s*%?$/.test(trimmed)) {
      return `\\$${trimmed}`;
    }
    const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
    const hasMathIndicators =
      /\\[a-zA-Z]+/.test(trimmed) ||
      /[=<>_^{}]/.test(trimmed) ||
      /\d\s*[+\-*/]\s*\d/.test(trimmed) ||
      /\b[a-zA-Z]\s*[+\-*/=]/.test(trimmed);
    if (wordCount >= 6 && !hasMathIndicators) {
      return inner;
    }
    mathBlocks.push(`$${sanitizeMathInner(inner)}$`);
    return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`;
  });

  // ===== STEP 5: Wrap raw LaTeX commands not yet delimited =====
  const wrapInline = (expr: string) => `$${sanitizeMathInner(expr)}$`;
  const wrapBlock = (expr: string) => `$$${sanitizeMathInner(expr)}$$`;

  result = result.replace(
    /(?<![\$%])\\frac\s*(\{(?:[^{}]|\{[^{}]*\})*\})\s*(\{(?:[^{}]|\{[^{}]*\})*\})/g,
    (match) => wrapInline(match),
  );

  result = result.replace(
    /(?<![\$%])\\sqrt\s*(?:\[([^\]]*)\])?\s*(\{(?:[^{}]|\{[^{}]*\})*\})/g,
    (match) => wrapInline(match),
  );

  result = result.replace(
    /(?<![\$%])\\binom\s*(\{(?:[^{}]|\{[^{}]*\})*\})\s*(\{(?:[^{}]|\{[^{}]*\})*\})/g,
    (match) => wrapInline(match),
  );

  result = result.replace(
    /(?<![\$%])\\(?:text|mathbb|mathcal|mathrm|mathbf|mathit)\s*(\{(?:[^{}]|\{[^{}]*\})*\})/g,
    (match) => wrapInline(match),
  );

  // Extended math function list
  result = result.replace(
    /(?<![\$%])(\\(?:sum|int|iint|iiint|oint|lim|prod|max|min|sup|inf|log|ln|lg|exp|sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|sinh|cosh|tanh|det|dim|ker|gcd|lcm|deg|hom|arg|Pr|Re|Im)(?:[_^]\{[^}]*\}|[_^]\w)*(?:\s*(?:\\[a-zA-Z]+(?:\{[^}]*\})?|[a-zA-Z0-9+\-*/=().,\s])+)?)/g,
    (match) => (/[\\]/.test(match) ? wrapInline(match.trim()) : match),
  );

  // Extended Greek + math symbols list
  result = result.replace(
    /(?<![\$%\\a-zA-Z])(\\(?:alpha|beta|gamma|delta|epsilon|varepsilon|theta|vartheta|lambda|mu|nu|sigma|pi|omega|phi|varphi|psi|rho|tau|eta|zeta|xi|kappa|iota|chi|upsilon|Gamma|Delta|Theta|Lambda|Sigma|Pi|Omega|Phi|Psi|Xi|infty|partial|nabla|pm|mp|times|cdot|div|neq|leq|geq|approx|equiv|sim|simeq|propto|perp|parallel|rightarrow|leftarrow|Rightarrow|Leftarrow|leftrightarrow|Leftrightarrow|forall|exists|nexists|in|notin|subset|supset|subseteq|supseteq|cup|cap|emptyset|varnothing|ldots|cdots|ddots|vdots|prime|circ|bullet|star|dagger|aleph|beth|ell|hbar|imath|jmath|wp|complement|triangle|angle|measuredangle|sphericalangle|square|blacksquare|lozenge|bigcirc|oplus|ominus|otimes|odot|oslash|boxplus|boxtimes|vee|wedge|neg|lnot|top|bot|vdash|dashv|models|cong|triangleq|doteq|circeq|bumpeq|ll|gg|prec|succ|preceq|succeq|nleq|ngeq|nless|ngtr|nprec|nsucc|lfloor|rfloor|lceil|rceil|langle|rangle|uparrow|downarrow|Uparrow|Downarrow|updownarrow|Updownarrow|nearrow|searrow|swarrow|nwarrow|mapsto|longmapsto|hookleftarrow|hookrightarrow|leadsto|rightsquigarrow|multimap|twoheadrightarrow|twoheadleftarrow|rightleftharpoons|leftrightharpoons))(?![a-zA-Z{])/g,
    (match) => wrapInline(match),
  );

  // Extended accent/decoration commands
  result = result.replace(
    /(?<![\$%])\\(?:overline|underline|hat|bar|vec|tilde|dot|ddot|widehat|widetilde|overbrace|underbrace|overleftarrow|overrightarrow|overleftrightarrow|boxed|cancel|bcancel|xcancel|sout)\s*(\{(?:[^{}]|\{[^{}]*\})*\})/g,
    (match) => wrapInline(match),
  );

  result = result.replace(
    /(?<![\$%])(\b[a-zA-Z]\w*(?:[_^]\{[^}]+\})+)/g,
    (match) => wrapInline(match),
  );

  // Wrap \begin{...}...\end{...} environments
  result = result.replace(
    /(?<![\$%])(\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\})/g,
    (match) => wrapBlock(match),
  );

  // ===== STEP 6: Final cleanup for dangling currency wrappers =====
  result = result.replace(
    /\\\$(\d[\d,]*(?:\.\d+)?)(\s*%?)\$(?!\w)/g,
    (_m, amount: string, suffix: string) => `\\$${amount}${suffix}`,
  );

  // ===== STEP 7: Restore protected math =====
  result = result.replace(/%%MATH_BLOCK_(\d+)%%/g, (_m, idx: string) => mathBlocks[parseInt(idx, 10)] ?? "");

  // Collapse accidental over-wrapping $$$$...$$$$ -> $$...$$
  result = result.replace(/\${3,}([^$]+?)\${3,}/g, "$$$1$$");

  // ===== STEP 8: Restore code blocks =====
  result = result.replace(/%%INLINE_CODE_(\d+)%%/g, (_m, idx: string) => inlineCodeBlocks[parseInt(idx, 10)] ?? "");
  result = result.replace(/%%CODE_BLOCK_(\d+)%%/g, (_m, idx: string) => fencedBlocks[parseInt(idx, 10)] ?? "");

  return result;
}

/**
 * Fixes unbalanced \left/\right pairs inside a math expression.
 */
function balanceLeftRight(mathExpr: string): string {
  const leftCount = (mathExpr.match(/\\left[\s]*[(\[{|.]/g) || []).length;
  const rightCount = (mathExpr.match(/\\right[\s]*[)\]}|.]/g) || []).length;
  if (leftCount > rightCount) {
    mathExpr += "\\right.".repeat(leftCount - rightCount);
  } else if (rightCount > leftCount) {
    mathExpr = "\\left.".repeat(rightCount - leftCount) + mathExpr;
  }
  return mathExpr;
}

/**
 * Second-pass validator that catches issues fixMathDelimiters may have missed.
 */
export function repairMathOutput(content: string): string {
  if (!content) return content;

  let result = content;

  // Protect code blocks
  const codeBlocks: string[] = [];
  result = result.replace(/```[\s\S]*?```/g, (m) => {
    codeBlocks.push(m);
    return `%%REPAIR_CODE_${codeBlocks.length - 1}%%`;
  });
  const inlineCode: string[] = [];
  result = result.replace(/`[^`]+`/g, (m) => {
    inlineCode.push(m);
    return `%%REPAIR_INLINE_${inlineCode.length - 1}%%`;
  });

  // Protect existing math blocks
  const existingMath: string[] = [];
  result = result.replace(/\$\$[\s\S]*?\$\$/g, (m) => {
    existingMath.push(m);
    return `%%EXISTING_MATH_${existingMath.length - 1}%%`;
  });
  result = result.replace(/(?<!\\)\$(?:\\.|[^$\n])+?\$/g, (m) => {
    existingMath.push(m);
    return `%%EXISTING_MATH_${existingMath.length - 1}%%`;
  });

  // 1. Fix orphan backslash-number: \189.04 → \$189.04  (but not valid commands like \alpha)
  result = result.replace(/(?<![a-zA-Z\\])\\(\d[\d,]*(?:\.\d+)?)/g, (_m, num: string) => `\\$${num}`);

  // 1b. Fix "= \10,000$" or "= \9,900$" patterns (backslash-number-dollar)
  result = result.replace(/\\(\d[\d,]*(?:\.\d+)?)\$/g, (_m, num: string) => `\\$${num}`);

  // 2. Fix "VARIABLE = \frac{...}{...}" not wrapped in math delimiters
  result = result.replace(
    /(?<![\$%])([A-Z][A-Za-z]*\s*=\s*\\(?:frac|sqrt|sum|int|prod)\s*(?:\{(?:[^{}]|\{[^{}]*\})*\}){1,2}(?:\s*[=+\-*/]\s*(?:\\(?:frac|sqrt)\s*(?:\{(?:[^{}]|\{[^{}]*\})*\}){1,2}|\d[\d,.]*(?:\s*%?)|\w+))*)/g,
    (match) => `$$${match}$$`,
  );

  // 3. Catch remaining bare currency $
  result = result.replace(/(?<!\\)\$(\d[\d,]*(?:\.\d+)?)\b/g, '\\$$1');

  // 4. Fix double-escaped currency: \\$100 → \$100
  result = result.replace(/\\\\(\$\d)/g, '\\$1');

  // 5. Fix "$1%" or "$15%" patterns
  result = result.replace(/(?<!\\)\$(\d[\d,]*(?:\.\d+)?)\s*%/g, '\\$$1%');

  // 6. Clean up spacing issues
  result = result.replace(/\$\s+\$([^$]+)\$\s+\$/g, '$$$1$$');

  // 7. Fix common AI typos in LaTeX commands
  result = result.replace(/\\frak\b/g, '\\frac');
  result = result.replace(/\\sqr\b(?!t)/g, '\\sqrt');
  result = result.replace(/\\lamda\b/g, '\\lambda');
  result = result.replace(/\\teta\b/g, '\\theta');
  result = result.replace(/\\Deta\b/g, '\\Delta');

  // Restore existing math
  result = result.replace(/%%EXISTING_MATH_(\d+)%%/g, (_m, i: string) => existingMath[parseInt(i, 10)] ?? "");

  // Restore code blocks
  result = result.replace(/%%REPAIR_INLINE_(\d+)%%/g, (_m, i: string) => inlineCode[parseInt(i, 10)] ?? "");
  result = result.replace(/%%REPAIR_CODE_(\d+)%%/g, (_m, i: string) => codeBlocks[parseInt(i, 10)] ?? "");

  return result;
}

/**
 * Final sanitization pass on math blocks before rendering.
 * Fixes common KaTeX-breaking patterns inside $...$ and $$...$$ blocks.
 */
function sanitizeMathBlocks(content: string): string {
  // Fix block math
  let result = content.replace(/\$\$([\s\S]*?)\$\$/g, (_m, inner: string) => {
    let fixed = inner;
    fixed = normalizeRawMathSyntax(fixed);
    fixed = fixed.replace(/\$\\(times|cdot|div|pm|mp|approx|neq|leq|geq|equiv|sim)\$/g, "\\$1");
    fixed = balanceLeftRight(fixed);
    // Remove stray markdown bold/italic inside math
    fixed = fixed.replace(/\*\*([^*]+)\*\*/g, '\\textbf{$1}');
    fixed = fixed.replace(/\*([^*]+)\*/g, '$1');
    // Fix double backslashes that aren't line breaks
    fixed = fixed.replace(/\\\\(?!\s*\\|$|\n)/g, '\\\\ ');
    return `$$${fixed}$$`;
  });

  // Fix inline math
  result = result.replace(/(?<!\\)\$((?:\\.|[^$\n])+?)\$/g, (_m, inner: string) => {
    let fixed = inner;
    fixed = normalizeRawMathSyntax(fixed);
    fixed = fixed.replace(/\$\\(times|cdot|div|pm|mp|approx|neq|leq|geq|equiv|sim)\$/g, "\\$1");
    fixed = balanceLeftRight(fixed);
    fixed = fixed.replace(/\*\*([^*]+)\*\*/g, '\\textbf{$1}');
    fixed = fixed.replace(/\*([^*]+)\*/g, '$1');
    return `$${fixed}$`;
  });

  return result;
}

/**
 * Normalizes common malformed math syntax produced by AI before KaTeX parsing.
 */
function normalizeRawMathSyntax(content: string): string {
  if (!content) return content;

  let normalized = content;

  // /frac, /sqrt, /sum ... -> \frac, \sqrt, \sum
  normalized = normalized.replace(
    /(?<!\\)\/(frac|sqrt|sum|int|prod|lim|alpha|beta|gamma|delta|theta|lambda|sigma|pi|mu|omega)\b/g,
    "\\\\$1",
  );

  // sqrt(x+y) -> \sqrt{x+y}
  normalized = normalized.replace(/(?<![\\a-zA-Z])sqrt\s*\(([^()\n]+)\)/g, "\\\\sqrt{$1}");

  // \frac{a}\{b} or \sqrt\{x} -> proper braced form
  normalized = normalized.replace(/\\frac\s*\{([^{}]*)\}\s*\\\{([^{}]*)\}/g, "\\frac{$1}{$2}");
  normalized = normalized.replace(/\\sqrt\s*\\\{([^{}]*)\}/g, "\\sqrt{$1}");

  // Remove inline-math wrappers around operators that were inserted inside equations
  normalized = normalized.replace(/\$\\(times|cdot|div|pm|mp|approx|neq|leq|geq|equiv|sim)\$/g, "\\$1");

  return normalized;
}

/**
 * NUCLEAR SAFETY NET: Catches any LaTeX command that leaked through ALL previous passes.
 * Runs line-by-line to avoid cross-line regex issues.
 * If a line contains a backslash-command (like \frac, \sqrt, \sum, etc.) that is NOT
 * inside $...$ or $$...$$, wrap the entire math-like segment.
 */
function nuclearSafetyNet(content: string): string {
  if (!content) return content;

  // Protect code blocks first
  const codeBlocks: string[] = [];
  let result = content.replace(/```[\s\S]*?```/g, (m) => {
    codeBlocks.push(m);
    return `%%NUCLEAR_CODE_${codeBlocks.length - 1}%%`;
  });
  const inlineCode: string[] = [];
  result = result.replace(/`[^`]+`/g, (m) => {
    inlineCode.push(m);
    return `%%NUCLEAR_INLINE_${inlineCode.length - 1}%%`;
  });

  // === PRE-PASS: Merge equation lines with fragmented $\times$ etc. ===
  // Pattern: "VAR = expr $\times$ expr" or "expr $\times$ expr = result"
  // These should be single math expressions, not fragments
  result = mergeFragmentedEquations(result);

  // Process line by line
  const lines = result.split('\n');
  const processedLines = lines.map(line => {
    if (/%%NUCLEAR_/.test(line)) return line;
    if (/^\$\$/.test(line.trim())) return line;

    const dangerousCommands = /\\(?:frac|sqrt|sum|int|iint|iiint|oint|prod|lim|binom|begin|end|overline|underline|vec|hat|bar|tilde|dot|ddot|boxed|cancel|mathbb|mathcal|mathrm|mathbf|mathit|operatorname)\b/;

    if (!dangerousCommands.test(line)) return line;

    let stripped = line;
    stripped = stripped.replace(/\$\$[^$]*\$\$/g, '');
    stripped = stripped.replace(/\$(?:\\.|[^$\n])*?\$/g, '');

    if (!dangerousCommands.test(stripped)) return line;

    let fixed = line;
    fixed = wrapBareLatexInLine(fixed);

    return fixed;
  });

  result = processedLines.join('\n');

  // Restore code
  result = result.replace(/%%NUCLEAR_INLINE_(\d+)%%/g, (_m, i: string) => inlineCode[parseInt(i, 10)] ?? "");
  result = result.replace(/%%NUCLEAR_CODE_(\d+)%%/g, (_m, i: string) => codeBlocks[parseInt(i, 10)] ?? "");

  // Final: collapse any triple+ dollar signs from overwrapping
  result = result.replace(/\${3,}([\s\S]+?)\${3,}/g, '$$$1$$');
  result = fixOddDollarSigns(result);

  return result;
}

/**
 * Merges fragmented equation lines where $\times$, $\cdot$, $\approx$ etc.
 * appear between bare LaTeX expressions, creating broken rendering.
 * Example: "i_{discount} = \frac{...}{...} $\times$ \frac{...}{...}"
 * becomes: "$i_{discount} = \frac{...}{...} \times \frac{...}{...}$"
 */
function mergeFragmentedEquations(content: string): string {
  const lines = content.split('\n');
  const merged = lines.map(line => {
    if (/%%/.test(line)) return line;
    if (/^\s*\$\$/.test(line)) return line;
    // Skip table header/separator lines
    if (/^\s*\|[\s-:]+\|/.test(line)) return line;

    // Detect lines with mixed bare LaTeX + inline $\cmd$ fragments
    // Must have at least one bare \frac/\sqrt AND at least one $\times$ or similar
    const hasBareLatex = /(?<!\$)\\(?:frac|sqrt|sum|int|prod|binom)\s*\{/.test(line);
    const hasFragmentedOp = /\$\\(?:times|cdot|div|pm|approx|neq|leq|geq)\$/.test(line);
    
    if (!hasBareLatex || !hasFragmentedOp) {
      // Also handle: "PV = 50 $\times$ 3.46516 + 792.09" (no bare LaTeX but equation-like)
      const isEquationLine = /^[A-Za-z_]\w*(?:\{[^}]*\})?\s*=\s*[\d.]+\s*\$\\(?:times|cdot)\$/.test(line.trim());
      if (!isEquationLine) return line;
    }

    // This line has fragmented math. Convert all $\cmd$ to bare \cmd and wrap whole expression
    let fixed = line;
    
    // Find the math portion of the line (may be preceded by text like "**Example:**" etc.)
    // Strategy: find where math starts (first identifier/number before = or first \command)
    const mathStartMatch = fixed.match(/(?:^|(?<=\s|:|\*))([a-zA-Z_]\w*(?:_\{[^}]*\})?\s*=|\\(?:frac|sqrt|sum|int|prod))/);
    if (!mathStartMatch || mathStartMatch.index === undefined) return line;
    
    const prefix = fixed.slice(0, mathStartMatch.index);
    let mathPart = fixed.slice(mathStartMatch.index);
    
    // Unwrap $\cmd$ fragments into bare commands
    mathPart = mathPart.replace(/\$\\(times|cdot|div|pm|mp|approx|neq|leq|geq|equiv|sim)\$/g, '\\$1');
    // Also unwrap $\approx$ patterns  
    mathPart = mathPart.replace(/\$\\([a-zA-Z]+)\$/g, '\\$1');
    
    // Trim trailing non-math text (sentences starting with uppercase after the equation)
    const trailingTextMatch = mathPart.match(/(\s+[A-Z][a-z]{2,}\s+[a-z])/);
    let suffix = '';
    if (trailingTextMatch && trailingTextMatch.index !== undefined) {
      suffix = mathPart.slice(trailingTextMatch.index);
      mathPart = mathPart.slice(0, trailingTextMatch.index);
    }
    
    // Check if already fully wrapped
    if (/^\$[^$]/.test(mathPart.trim()) && /[^$]\$$/.test(mathPart.trim())) return line;
    
    // Wrap the math portion
    return `${prefix}$${mathPart.trim()}$${suffix}`;
  });
  return merged.join('\n');
}

/**
 * Wraps bare LaTeX commands that appear outside of math delimiters within a single line.
 */
function wrapBareLatexInLine(line: string): string {
  // Protect already-delimited math
  const mathSegments: string[] = [];
  let processed = line.replace(/\$\$[^$]*?\$\$/g, (m) => {
    mathSegments.push(m);
    return `%%SEG_${mathSegments.length - 1}%%`;
  });
  processed = processed.replace(/\$(?:\\.|[^$\n])*?\$/g, (m) => {
    mathSegments.push(m);
    return `%%SEG_${mathSegments.length - 1}%%`;
  });

  // Now wrap any remaining bare LaTeX expressions
  // Match: optional "var = " prefix, then \command{...}{...} possibly chained with operators and more commands/numbers
  processed = processed.replace(
    /(?:(?:[a-zA-Z_]\w*\s*=\s*)?\\(?:frac|sqrt|binom)\s*(?:\{(?:[^{}]|\{[^{}]*\})*\}){1,2}(?:\s*[=+\-*/±×·]\s*(?:\\(?:frac|sqrt|binom)\s*(?:\{(?:[^{}]|\{[^{}]*\})*\}){1,2}|(?:-?\d[\d,.]*)|(?:[a-zA-Z_]\w*)))*)/g,
    (match) => {
      // Don't wrap if it's already inside a segment placeholder
      if (/%%SEG_/.test(match)) return match;
      return `$${match}$`;
    }
  );

  // Also catch bare \sum, \int, \prod, \lim with sub/superscripts
  processed = processed.replace(
    /(?<![\$%])(\\(?:sum|int|iint|iiint|oint|prod|lim)(?:[_^]\{[^}]*\}|[_^]\w)*(?:\s*\\?[a-zA-Z0-9+\-*/=().,{}\s\\]+)*)/g,
    (match) => {
      if (/%%SEG_/.test(match)) return match;
      // Only wrap if it actually contains a backslash command
      if (!/\\[a-zA-Z]/.test(match)) return match;
      return `$${match.trim()}$`;
    }
  );

  // Catch bare \overline, \vec, etc with {content}
  processed = processed.replace(
    /(?<![\$%])\\(?:overline|underline|vec|hat|bar|tilde|dot|ddot|boxed|cancel|mathbb|mathcal|mathrm|mathbf)\s*\{(?:[^{}]|\{[^{}]*\})*\}/g,
    (match) => {
      if (/%%SEG_/.test(match)) return match;
      return `$${match}$`;
    }
  );

  // Catch bare \begin{...}...\end{...}
  processed = processed.replace(
    /(?<![\$%])\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\}/g,
    (match) => {
      if (/%%SEG_/.test(match)) return match;
      return `$$${match}$$`;
    }
  );

  // Restore math segments
  processed = processed.replace(/%%SEG_(\d+)%%/g, (_m, i: string) => mathSegments[parseInt(i, 10)] ?? "");

  return processed;
}

/**
 * Detects lines with an odd number of unescaped $ signs and attempts to fix them.
 * This catches cases where the AI produces malformed delimiter pairs.
 */
function fixOddDollarSigns(content: string): string {
  const lines = content.split('\n');
  const fixedLines = lines.map(line => {
    // Skip code placeholders
    if (/%%/.test(line)) return line;
    // Skip lines that are block math boundaries
    if (/^\s*\$\$/.test(line)) return line;

    // Count unescaped $ signs
    const dollars = line.match(/(?<!\\)\$/g);
    if (!dollars || dollars.length % 2 === 0) return line;

    // Odd number of $. Try to fix.
    // Common case: trailing lone $ or missing opening $
    // Strategy 1: If line ends with $ but content before it has LaTeX, add opening $
    if (/\\[a-zA-Z]+/.test(line) && /(?<!\\)\$$/.test(line)) {
      // Find the first LaTeX command not inside existing $...$ and add $ before it
      const match = line.match(/(?<![\$\\])\\(?:frac|sqrt|sum|int|prod|lim|alpha|beta|gamma|delta|theta|lambda|sigma|pi|omega|infty|partial|nabla|pm|times|cdot|leq|geq|neq|approx)/);
      if (match && match.index !== undefined) {
        return line.slice(0, match.index) + '$' + line.slice(match.index);
      }
    }
    // Strategy 2: If there's a lone $ at the start with LaTeX after it
    if (/^\$[^$]/.test(line) && /\\[a-zA-Z]+/.test(line) && dollars.length === 1) {
      return line + '$';
    }

    return line;
  });
  return fixedLines.join('\n');
}

/**
 * Single entry point for preparing AI-generated content for rendering.
 * Chains: fixMathDelimiters → repairMathOutput → sanitizeMathBlocks → nuclearSafetyNet → ensureMermaidFences
 */
export function prepareMathContent(content: string): string {
  if (!content) return content;
  let result = ensureMermaidFences(nuclearSafetyNet(sanitizeMathBlocks(repairMathOutput(fixMathDelimiters(content)))));
  // Split concatenated math blocks onto separate lines for readability
  result = splitConcatenatedMathBlocks(result);
  return result;
}

/**
 * Splits concatenated math blocks that appear on the same line.
 * AI often generates: "$a = 1$ $b = 2$ $c = 3$" which should be separate lines.
 * Also handles block math: "$$expr1$$ $$expr2$$" → separate lines.
 */
function splitConcatenatedMathBlocks(content: string): string {
  if (!content) return content;

  // Protect code blocks
  const codeBlocks: string[] = [];
  let result = content.replace(/```[\s\S]*?```/g, (m) => {
    codeBlocks.push(m);
    return `%%SPLIT_CODE_${codeBlocks.length - 1}%%`;
  });

  // Split consecutive block math: "$$...$$  $$...$$" or "$$...$$ text $$...$$"
  // Insert newlines between them
  result = result.replace(/\$\$([\s\S]*?)\$\$\s*(?:([a-zA-Z_]\w*\s*=\s*))?\$\$/g, (match) => {
    // More targeted: split any "$$...$$" followed by "$$...$$" on same line
    return match;
  });

  // Detect table rows (lines starting with |) so we never add blank lines between them
  const isTableRow = (l: string) => /^\s*\|/.test(l);

  // Process line by line: if a line has multiple $...$ math expressions concatenated, split them
  const lines = result.split('\n');
  const processed = lines.flatMap(line => {
    if (/%%SPLIT_CODE/.test(line)) return [line];
    if (/^\s*\$\$/.test(line.trim())) return [line];

    // Count inline math blocks on this line
    const inlineMathRegex = /\$(?:\\.|[^$\n])+?\$/g;
    const matches = line.match(inlineMathRegex);
    if (!matches || matches.length < 2) return [line];

    // Check if these are calculation steps (contain = signs)
    const calcSteps = matches.filter(m => /=/.test(m));
    if (calcSteps.length < 2) return [line];

    // Split: put each math block on its own line
    // First, extract text segments between math blocks
    const parts: string[] = [];
    let remaining = line;
    let lastIndex = 0;

    // Use a fresh regex for exec
    const splitRegex = /\$(?:\\.|[^$\n])+?\$/g;
    let m;
    const segments: { text: string; isMath: boolean }[] = [];

    while ((m = splitRegex.exec(remaining)) !== null) {
      if (m.index > lastIndex) {
        const between = remaining.slice(lastIndex, m.index).trim();
        if (between) segments.push({ text: between, isMath: false });
      }
      segments.push({ text: m[0], isMath: true });
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < remaining.length) {
      const tail = remaining.slice(lastIndex).trim();
      if (tail) segments.push({ text: tail, isMath: false });
    }

    // If we have multiple math segments with = signs, split them onto separate lines
    const mathCount = segments.filter(s => s.isMath && /=/.test(s.text)).length;
    if (mathCount >= 2) {
      const resultLines: string[] = [];
      for (const seg of segments) {
        if (seg.isMath) {
          // Convert inline $ to block $$ for multi-step calculations
          const inner = seg.text.slice(1, -1);
          resultLines.push(`$$${inner}$$`);
        } else if (seg.text && !/^[.:;,)}\]\s]+$/.test(seg.text.trim())) {
          // Keep meaningful text, drop orphan punctuation
          resultLines.push(seg.text);
        }
      }
      return ['', ...resultLines, ''];
    }

    return [line];
  });

  // Join with single newlines to preserve table structure, then normalize spacing
  result = processed.join('\n');
  // Ensure blank lines around $$ blocks for readability (but NOT inside tables)
  result = result.replace(/([^\n])\n(\$\$)/g, '$1\n\n$2');
  result = result.replace(/(\$\$)\n([^\n$])/g, '$1\n\n$2');
  // Clean up excessive blank lines
  result = result.replace(/\n{4,}/g, '\n\n\n');

  // Restore code blocks
  result = result.replace(/%%SPLIT_CODE_(\d+)%%/g, (_m, i: string) => codeBlocks[parseInt(i, 10)] ?? "");

  return result;
}

/**
 * Ensures mermaid diagram code is properly wrapped in fenced code blocks.
 * Handles diagrams with blank lines between sections (subgraphs, etc.).
 */
export function ensureMermaidFences(content: string): string {
  if (!content) return content;

  // Step 1: tag unfenced ``` blocks whose content starts with a mermaid keyword
  let result = content.replace(
    /```\s*\n((?:graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitgraph|mindmap)\b[\s\S]*?)```/g,
    "```mermaid\n$1```",
  );

  // Step 2: protect already-fenced blocks
  const fencedBlocks: string[] = [];
  result = result.replace(/```[\s\S]*?```/g, (match) => {
    fencedBlocks.push(match);
    return `%%FENCED_BLOCK_${fencedBlocks.length - 1}%%`;
  });

  // Step 3: detect bare mermaid diagrams (no fences) — supports blank lines inside
  const mermaidKeywords =
    /^((?:graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitgraph|mindmap)(?:\s+(?:TD|TB|BT|RL|LR))?)\s*$/;

  const lines = result.split('\n');
  const outputLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) { i++; continue; }
    // Skip already-protected blocks
    if (/%%FENCED_BLOCK_/.test(line)) {
      outputLines.push(line);
      i++;
      continue;
    }

    const headerMatch = line.trim().match(mermaidKeywords);
    if (headerMatch) {
      // Collect the body: indented lines OR blank lines, stop at first non-indented non-blank line
      const bodyLines: string[] = [];
      let j = i + 1;
      while (j < lines.length) {
        const bodyLine = lines[j];
        if (bodyLine === undefined) break;
        // Indented line or keyword like "end", "subgraph", "style", "classDef", "class", "click", "linkStyle"
        if (/^[ \t]+\S/.test(bodyLine) || /^\s*$/.test(bodyLine) || /^\s*(end|subgraph|style|classDef|class|click|linkStyle)\b/.test(bodyLine)) {
          bodyLines.push(bodyLine);
          j++;
        } else {
          break;
        }
      }

      // Trim trailing blank lines
      while (bodyLines.length > 0 && (bodyLines[bodyLines.length - 1] ?? '').trim() === '') {
        bodyLines.pop();
      }

      if (bodyLines.length > 0) {
        outputLines.push('```mermaid');
        outputLines.push(line.trim());
        outputLines.push(...bodyLines);
        outputLines.push('```');
        i = j;
        continue;
      }
    }

    outputLines.push(line);
    i++;
  }

  result = outputLines.join('\n');

  // Step 4: restore fenced blocks
  result = result.replace(/%%FENCED_BLOCK_(\d+)%%/g, (_m, idx) => fencedBlocks[parseInt(idx)] ?? "");

  return result;
}
